import React, { useEffect, useMemo, useRef, useState } from "react";
import { areaY, colorLegend, colorLegendItems, defineChart, lineY, ruleY } from "@tanstack/charts";
import { d3Curve } from "@tanstack/charts";
import { brushX, type BrushRange, type BrushXChange } from "@tanstack/charts/interaction/brush";
import { controlledSignal } from "@tanstack/charts/interaction/signal";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { scaleUtc } from "d3-scale";
import { curveStepAfter } from "d3-shape";

import { ChartFrame } from "./ChartFrame";
import { ChartLayout, ChartLegendPosition, COLORWAY, NamedSeries } from "./chartTheme";

interface LineRow {
    date: Date;
    value: number;
    series: string;
}

export function TimeSeriesLines({
    series,
    yFormat,
    yLabel,
    xLabel,
    showLegend = true,
    legendLayout = "columns",
    legendPosition = "top",
    zoom = false,
    includeZero = false,
    yAxis,
    curve = "linear",
    ariaLabel,
    ariaDescription,
    height = 320,
    width,
    style,
    className,
    onReady
}: {
    series: NamedSeries[];
    yFormat?: (value: number) => string;
    yLabel?: string;
    xLabel?: string;
    showLegend?: boolean;
    legendLayout?: "columns" | "wrap";
    legendPosition?: ChartLegendPosition;
    /** Drag across the plot to zoom the time axis into that range. */
    zoom?: boolean;
    includeZero?: boolean;
    /** Plotly-style y-axis options from {@link ChartLayout} (`autorange: "close"`, `range`, etc.). */
    yAxis?: ChartLayout["yaxis"];
    curve?: "linear" | "step";
    ariaLabel: string;
    ariaDescription?: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    className?: string;
    onReady?: () => void;
}): React.ReactElement {
    const drawn = series.filter((item) => item.points.length > 0);
    let extentStart: Date | null = null;
    let extentEnd: Date | null = null;
    for (const item of drawn) {
        for (const point of item.points) {
            if (!extentStart || point.date < extentStart) extentStart = point.date;
            if (!extentEnd || point.date > extentEnd) extentEnd = point.date;
        }
    }
    const extentKey = extentStart && extentEnd ? extentStart.getTime() + ":" + extentEnd.getTime() : "";
    const [zoomWindow, setZoomWindow] = useState<{ key: string; start: Date; end: Date } | null>(null);
    const activeWindow = zoom && zoomWindow && zoomWindow.key === extentKey ? zoomWindow : null;
    const frameRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const frame = frameRef.current;
        if (!zoom || !frame) return;
        // The brush overlay sits on the plot and the chart treats that as its own control, which
        // clears the hover tooltip. Leave the overlay out of hit testing, and start a brush from
        // the chart's pointer down so a drag still zooms.
        const onPointerDown = (event: PointerEvent) => {
            if (event.button !== 0) return;
            const overlay = frame.querySelector("[data-chart-brush] .overlay");
            if (!(overlay instanceof SVGElement)) return;
            const rect = overlay.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
                return;
            }
            overlay.dispatchEvent(
                new MouseEvent("mousedown", {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    clientX: event.clientX,
                    clientY: event.clientY
                })
            );
        };
        frame.addEventListener("pointerdown", onPointerDown);
        return () => frame.removeEventListener("pointerdown", onPointerDown);
    }, [zoom]);

    const definition = useMemo(() => {
        const rows: LineRow[] = [];
        for (const item of drawn) {
            for (const point of item.points) {
                rows.push({ date: point.date, value: point.value, series: item.name });
            }
        }
        const names = drawn.map((item) => item.name);
        const range = drawn.map((item, index) => item.color || COLORWAY[index % COLORWAY.length]);
        const curveOption = curve === "step" ? d3Curve(curveStepAfter) : undefined;
        const visibleStart = activeWindow ? activeWindow.start : extentStart;
        const visibleEnd = activeWindow ? activeWindow.end : extentEnd;
        let xScale: typeof scaleUtc = scaleUtc;
        if (zoom && visibleStart && visibleEnd && visibleStart.getTime() !== visibleEnd.getTime()) {
            // d3's ScaleTime instance is what the chart runs, but its range() overloads are not assignable to the chart scale type.
            xScale = scaleUtc().domain([visibleStart, visibleEnd]) as unknown as typeof scaleUtc;
        }
        let yScale: typeof scaleLinear | ReturnType<typeof scaleLinear> = scaleLinear;
        let yNice = !includeZero && !activeWindow;
        if (activeWindow && rows.length > 0) {
            let min = Infinity;
            let max = -Infinity;
            const startTime = activeWindow.start.getTime();
            const endTime = activeWindow.end.getTime();
            for (const row of rows) {
                const time = row.date.getTime();
                if (time < startTime || time > endTime) continue;
                if (row.value < min) min = row.value;
                if (row.value > max) max = row.value;
            }
            if (min !== Infinity) {
                let pad = (max - min) * 0.08;
                if (pad === 0) pad = Math.abs(max) * 0.05 || 1;
                yScale = scaleLinear().domain([min - pad, max + pad]);
                yNice = false;
            }
        } else if (rows.length > 0) {
            if (yAxis?.range && yAxis.range.length >= 2) {
                yScale = scaleLinear().domain([yAxis.range[0], yAxis.range[1]]);
                yNice = false;
            } else {
                let min = Infinity;
                let max = -Infinity;
                for (const row of rows) {
                    if (row.value < min) min = row.value;
                    if (row.value > max) max = row.value;
                }
                if (min !== Infinity) {
                    if (yAxis?.autorange === "close") {
                        let pad = (max - min) * 0.08;
                        if (pad === 0) pad = Math.abs(max) * 0.05 || 0.001;
                        yScale = scaleLinear().domain([min - pad, max + pad]);
                        yNice = false;
                    } else if (yAxis?.autorange === "tozero") {
                        yScale = scaleLinear().domain([Math.min(0, min), Math.max(0, max)]);
                        yNice = false;
                    } else if (includeZero) {
                        let minZero = 0;
                        let maxZero = 0;
                        for (const row of rows) {
                            if (row.value < minZero) minZero = row.value;
                            if (row.value > maxZero) maxZero = row.value;
                        }
                        yScale = scaleLinear().domain([minZero, maxZero]);
                        yNice = false;
                    }
                }
            }
        }
        const marks = [];
        const filled = drawn.filter((item) => item.fill);
        if (filled.length > 0) {
            const fillRows = rows.filter((row) => filled.some((item) => item.name === row.series));
            marks.push(
                decorative(
                    areaY(fillRows, {
                        x: "date",
                        y: "value",
                        z: "series",
                        color: "series",
                        fillOpacity: 0.18,
                        curve: curveOption
                    })
                )
            );
        }
        const solid = drawn.filter((item) => !item.dash);
        const dashed = drawn.filter((item) => item.dash);
        if (solid.length > 0) {
            const solidRows = rows.filter((row) => solid.some((item) => item.name === row.series));
            marks.push(
                lineY(solidRows, {
                    x: "date",
                    y: "value",
                    z: "series",
                    color: "series",
                    strokeWidth: 2.5,
                    curve: curveOption
                })
            );
        }
        if (dashed.length > 0) {
            const dashedRows = rows.filter((row) => dashed.some((item) => item.name === row.series));
            marks.push(
                lineY(dashedRows, {
                    x: "date",
                    y: "value",
                    z: "series",
                    color: "series",
                    strokeWidth: 2.5,
                    strokeDasharray: "4 3",
                    curve: curveOption
                })
            );
        }
        if (includeZero) marks.unshift(decorative(ruleY([0], { stroke: "#cbd5e1" })));
        return defineChart({
            marks,
            scales: {
                x: { scale: xScale, axis: { label: xLabel, line: false } },
                y: {
                    scale: yScale,
                    nice: yNice,
                    grid: true,
                    axis: { label: yLabel, line: false, ticks: yFormat ? { format: yFormat } : undefined }
                }
            },
            controls:
                zoom && visibleStart && visibleEnd && visibleStart.getTime() !== visibleEnd.getTime()
                    ? [
                          brushX({
                              range: controlledSignal<BrushRange<Date>, BrushXChange<Date>>({ start: visibleStart, end: visibleStart }, (next, context) => {
                                  if (context.reason.type !== "commit") return;
                                  const startTime = next.start instanceof Date ? next.start.getTime() : NaN;
                                  const endTime = next.end instanceof Date ? next.end.getTime() : NaN;
                                  const span = Math.abs(endTime - startTime);
                                  if (!Number.isFinite(span) || span < 24 * 60 * 60 * 1000) return;
                                  setZoomWindow({
                                      key: extentKey,
                                      start: new Date(Math.min(startTime, endTime)),
                                      end: new Date(Math.max(startTime, endTime))
                                  });
                              }),
                              keyboard: false,
                              ariaLabel: "Drag across the chart to zoom into a date range",
                              selectionStyle: { fill: "#2186c5", fillOpacity: 0.2, strokeOpacity: 0 },
                              handleStyle: { fillOpacity: 0 }
                          })
                      ]
                    : undefined,
            color: {
                domain: names,
                range,
                legend:
                    showLegend && names.length > 0
                        ? colorLegend({
                              placement: legendPosition,
                              ...(legendLayout === "wrap"
                                  ? {
                                        items: colorLegendItems({
                                            justify: "start",
                                            gap: 18,
                                            rowGap: 8,
                                            label: { fontSize: 12 }
                                        })
                                    }
                                  : {})
                          })
                        : undefined
            },
            tooltip: {
                use: tooltip,
                content: (points) => {
                    const point = points[0];
                    if (!point) return { rows: [] };
                    let date = "";
                    if (point.xValue instanceof Date && !Number.isNaN(point.xValue.getTime())) {
                        date = point.xValue.toISOString().replace("T00:00:00.000Z", "");
                    } else if (typeof point.xValue !== "undefined") {
                        date = String(point.xValue);
                    }
                    let valueText = "";
                    if (typeof point.yValue === "number" && Number.isFinite(point.yValue)) {
                        valueText = yFormat ? yFormat(point.yValue) : point.yValue.toLocaleString();
                    }
                    const rows = [];
                    if (date || valueText) rows.push({ label: date, value: valueText });
                    return {
                        title: point.groupLabel,
                        color: point.color,
                        rows
                    };
                }
            }
        });
    }, [activeWindow, curve, drawn, extentEnd, extentKey, extentStart, includeZero, legendLayout, legendPosition, showLegend, xLabel, yAxis, yFormat, yLabel, zoom]);

    if (drawn.length === 0) return <div />;

    return (
        <div ref={frameRef}>
            {zoom ? <style>{`[data-chart-brush], [data-chart-brush] * { pointer-events: none !important; }`}</style> : null}
            {activeWindow ? (
                <button
                    type="button"
                    onClick={() => {
                        setZoomWindow(null);
                    }}
                    style={{
                        margin: "0 0 8px 8px",
                        padding: "4px 10px",
                        border: "1px solid #cbd5e1",
                        borderRadius: 4,
                        background: "#fff",
                        cursor: "pointer"
                    }}
                >
                    Reset zoom
                </button>
            ) : null}
            <ChartFrame
                definition={definition}
                ariaLabel={ariaLabel}
                ariaDescription={ariaDescription}
                height={height}
                width={width}
                style={style}
                className={className}
                onReady={onReady}
            />
        </div>
    );
}
